/**
 * The sticky summary rail — the live consequence of what has been typed.
 *
 * The wizard told you what you had entered; this tells you what it MEANS. Every
 * line is derived, none of it is an input, and the checklist at the bottom
 * replaces the stepper: the same at-a-glance progress, but each entry navigates
 * instead of gating, and the Create button below it is never disabled.
 *
 * `aria-live="off"` is deliberate — announcing the consequence of every
 * keystroke would make the form unusable with a screen reader. The Review card
 * is the read-back; the error summary is the only live region on the page.
 */

import { Button, Card, Icon, Meter, RoleChip } from '@w7/ui'
import { Amount } from '@/app/bits'
import { money, pct } from '@/lib/format'
import { GAME_LABEL, ROLES, ROLE_LABEL, roleDepth } from '@/data/types'
import { SEG_REST, SEG_THEM, SEG_YOU, SplitBar, type SectionCtx } from './sections'
import { enabledGames, parseNumber } from './summary'

export type ChecklistState = 'done' | 'todo' | 'error'

export interface ChecklistEntry {
  id: string
  label: string
  state: ChecklistState
}

export interface CreateSubmitProps {
  label: string
  busy: boolean
  /** Why the primary would not go through, in plain words. Never a disabled button. */
  blockReason: string | null
}

/**
 * The primary action, rendered twice — once in the rail for desktop, once in the
 * phone action bar — with exactly one of the two visible (and therefore exactly
 * one in the accessibility tree) at any width.
 *
 * It is `type="submit"` and it is NEVER disabled. A permanently grey primary
 * with its explanation somewhere else is precisely the trap the old form fell
 * into; pressing it on an incomplete form answers every field, renders the error
 * summary and moves focus to the first invalid control, which is both more
 * discoverable and the standard accessible pattern. `busy` still blocks the
 * double submission that disabling used to prevent by accident.
 */
export function CreateSubmit({ label, busy, blockReason }: CreateSubmitProps) {
  return (
    <div className="w7-create-submit">
      <Button type="submit" variant="primary" icon="plus" busy={busy}>
        {label}
      </Button>
      {blockReason ? <p className="w7-create-block">{blockReason}</p> : null}
    </div>
  )
}

export interface SummaryRailProps {
  ctx: SectionCtx
  checklist: ChecklistEntry[]
  onNavigate: (sectionId: string) => void
  submit: CreateSubmitProps
}

export function SummaryRail({ ctx, checklist, onNavigate, submit }: SummaryRailProps) {
  const { parent, role, values, summary } = ctx
  const isMember = role === 'player'
  const levelGap = roleDepth(role) - roleDepth(parent.role)
  const games = enabledGames(values.games, GAME_LABEL)
  const minValue = parseNumber(values.minBet)
  const maxValue = parseNumber(values.maxBet)
  const usage = parent.balance > 0 ? summary.allocating / parent.balance : 0

  return (
    <aside className="w7-create-rail" aria-label="What you are creating" aria-live="off">
      <Card title="What you are creating">
        <div className="w7-create-railgroup">
          <div className="w7-row" style={{ gap: 'var(--w7-s2)' }}>
            <RoleChip role={role} label={ROLE_LABEL[role]} />
          </div>
          <p className="w7-create-railnote">
            Level {roleDepth(role) + 1} of {ROLES.length},{' '}
            {levelGap === 1 ? 'one level' : `${levelGap} levels`} below you, directly under{' '}
            {parent.username}.
          </p>
        </div>

        <div className="w7-create-railgroup">
          <span className="w7-micro">What they can hold</span>
          <div className="w7-create-railrow">
            <span>Credit limit</span>
            <Amount value={summary.creditLimit} />
          </div>
          <Meter
            value={usage}
            tone={summary.overAllocated ? 'loss' : undefined}
            label={`${money(summary.allocating)} of your ${money(parent.balance)}`}
          />
          <div className="w7-create-railrow">
            <span>Left with you</span>
            <Amount value={summary.remaining} />
          </div>
        </div>

        <div className="w7-create-railgroup">
          <span className="w7-micro">What you keep, what they keep</span>
          <SplitBar
            label={`You keep ${pct(summary.yourShare, 2)}, they keep ${pct(summary.theirShare, 2)}`}
            segments={[
              { id: 'you', value: summary.yourShare, color: SEG_YOU },
              { id: 'them', value: summary.theirShare, color: SEG_THEM },
              { id: 'rest', value: summary.restOfChain, color: SEG_REST },
            ]}
          />
          <p className="w7-create-railnote">
            You {pct(summary.yourShare, 2)} · They {pct(summary.theirShare, 2)} · Rest of the chain{' '}
            {pct(summary.restOfChain, 2)}
          </p>
          {/* Percentages are abstract; a worked figure is not. The note under it
              is not decoration — the money invariant applies to made-up numbers
              too, so an operator must never be able to read this as a ledger
              projection. */}
          <p className="w7-create-illus">
            On a <strong>{money(summary.illustration.base, { round: true })}</strong> member loss,{' '}
            <strong>{money(summary.illustration.toYou, { round: true })}</strong> reaches you and{' '}
            <strong>{money(summary.illustration.toThem, { round: true })}</strong> reaches them.
            <span className="w7-create-illus-note">illustration, not a projection</span>
          </p>
        </div>

        <div className="w7-create-railgroup">
          <span className="w7-micro">Commission passed down</span>
          <div className="w7-create-railrow">
            <span>Fancy</span>
            <span>
              {pct(parseNumber(values.fancy), 2)} of your {pct(parent.commission.fancy, 2)}
            </span>
          </div>
          <div className="w7-create-railrow">
            <span>Bookmaker</span>
            <span>
              {pct(parseNumber(values.bookmaker), 2)} of your {pct(parent.commission.bookmaker, 2)}
            </span>
          </div>
          <div className="w7-create-railrow">
            <span>Match odds</span>
            <span>
              {pct(parseNumber(values.matchOdds), 2)} of your {pct(parent.commission.matchOdds, 2)}
            </span>
          </div>
        </div>

        <div className="w7-create-railgroup">
          <span className="w7-micro">Bet limits</span>
          <div className="w7-create-railrow">
            <span>Per bet</span>
            {isMember ? (
              <span>
                <Amount value={minValue} /> – <Amount value={maxValue} />
              </span>
            ) : (
              <span>Set per member</span>
            )}
          </div>
        </div>

        <div className="w7-create-railgroup">
          <span className="w7-micro">Games</span>
          <div className="w7-create-railrow">
            <span>{isMember ? `${games.on} of ${games.total} enabled` : 'Inherits yours'}</span>
          </div>
          {isMember ? (
            <p className="w7-create-railnote">{games.names.join(', ') || 'None enabled'}</p>
          ) : (
            // The wizard's best idea, stated where the absent section would have
            // been: an agent is not asked for numbers that will never apply to it.
            <p className="w7-create-railnote">
              Bet limits and game access are member settings. An agent inherits yours and sets them
              on the members it creates.
            </p>
          )}
        </div>

        <div className="w7-create-railgroup">
          <span className="w7-micro">Checklist</span>
          <ul className="w7-create-check">
            {checklist.map((entry) => (
              <li key={entry.id}>
                {/* A real anchor, so it still lands somewhere sensible without
                    the smooth scroll, with an onClick that also focuses the
                    section's first control — mouse and keyboard end up in the
                    same place. This is the whole replacement for the stepper:
                    the same at-a-glance progress, navigating instead of gating. */}
                <a
                  className="w7-create-checklink"
                  href={`#${entry.id}`}
                  data-state={entry.state}
                  onClick={(event) => {
                    event.preventDefault()
                    onNavigate(entry.id)
                  }}
                >
                  <span className="w7-create-checkicon" aria-hidden="true">
                    <Icon
                      name={
                        entry.state === 'done'
                          ? 'check'
                          : entry.state === 'error'
                            ? 'error'
                            : 'minus'
                      }
                      size={12}
                    />
                  </span>
                  <span className="w7-truncate">{entry.label}</span>
                  <span className="w7-sr">
                    {entry.state === 'done'
                      ? '— done'
                      : entry.state === 'error'
                        ? '— needs fixing'
                        : '— not answered yet'}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </Card>

      <div className="w7-create-railsubmit">
        <CreateSubmit {...submit} />
      </div>
    </aside>
  )
}
